import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { MapPin, Wallet, Users, CheckCircle2, RotateCcw, Plus, Inbox, Zap } from "lucide-react";

export default function ClientSpace() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sectors: [] });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [m, r] = await Promise.all([api.get("/meta"), api.get("/client/requests")]);
      setMeta(m.data);
      setItems(r.data);
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading || !user) return null;

  const sectorLabel = (id) => {
    const s = meta.sectors.find((x) => x.id === id);
    return s ? (lang === "fr" ? s.fr : s.en) : id;
  };

  const toggleClose = async (req) => {
    try {
      const res = await api.post(`/requests/${req.id}/close`);
      setItems((prev) => prev.map((r) => r.id === req.id ? { ...r, status: res.data.status } : r));
      toast.success(res.data.status === "closed"
        ? (lang === "fr" ? "Demande fermée" : "Request closed")
        : (lang === "fr" ? "Demande rouverte" : "Request reopened"));
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("my_requests")}</h1>
          <p className="text-muted-foreground mt-1">{lang === "fr" ? "Suivez et gérez vos demandes publiées." : "Track and manage your posted requests."}</p>
        </div>
        <Button data-testid="client-new-request-btn" onClick={() => navigate("/post")}>
          <Plus className="h-4 w-4 mr-1.5" /> {t("nav_post")}
        </Button>
      </div>

      {busy ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="client-empty">
          <Inbox className="h-10 w-10 mx-auto mb-4 opacity-50" />
          <p className="mb-6">{lang === "fr" ? "Vous n'avez pas encore publié de demande." : "You haven't posted any requests yet."}</p>
          <Button onClick={() => navigate("/post")} data-testid="client-empty-post-btn">{t("nav_post")}</Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="client-requests-list">
          {items.map((req) => (
            <div key={req.id} data-testid={`client-request-${req.id}`}
              className="rounded-2xl border border-border bg-card p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <Badge variant="secondary" className="rounded-full">{sectorLabel(req.sector)}</Badge>
                  {req.urgency === "urgent" && (
                    <Badge className="rounded-full bg-destructive text-destructive-foreground gap-1"><Zap className="h-3 w-3" />{t("urgent_badge")}</Badge>
                  )}
                  <Badge variant={req.status === "closed" ? "outline" : "default"}
                    className={`rounded-full ${req.status === "open" ? "bg-emerald-600" : ""}`}>
                    {req.status === "open" ? (lang === "fr" ? "Ouverte" : "Open") : (lang === "fr" ? "Fermée" : "Closed")}
                  </Badge>
                </div>
                <h3 className="font-display text-lg font-semibold truncate">{req.title}</h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1.5">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{req.location}</span>
                  {req.budget && <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />{req.budget}</span>}
                  <span className="inline-flex items-center gap-1.5 text-primary font-medium">
                    <Users className="h-3.5 w-3.5" />{req.interested_count} {lang === "fr" ? "pro(s) intéressé(s)" : "interested pro(s)"}
                  </span>
                </div>
              </div>
              <Button variant="outline" size="sm" data-testid={`client-close-btn-${req.id}`} onClick={() => toggleClose(req)} className="shrink-0">
                {req.status === "open"
                  ? <><CheckCircle2 className="h-4 w-4 mr-1.5" />{lang === "fr" ? "Fermer" : "Close"}</>
                  : <><RotateCcw className="h-4 w-4 mr-1.5" />{lang === "fr" ? "Rouvrir" : "Reopen"}</>}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
